import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { Card, Divider, Spin, Typography } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertBanner } from '../../../../AlertBanner';
import { SignInButton } from '../../../../SignInButton';
import { UserContext } from '../../../../UserContext';
import { AgentSection } from '../../../../components/client/agent-section';
import { AppointmentsConfig } from '../../../../components/client/appointment-types-config';
import { BrandingConfig } from '../../../../components/client/branding-config';
import { ClientConfig } from '../../../../components/client/client-config';
import { ClientFeatures } from '../../../../components/client/client-features';
import { ClientNavigation } from '../../../../components/client/client-navigation';
import { CredentialConfig } from '../../../../components/client/external-api-config';
import { StagesConfig } from '../../../../components/client/stages-config';
import '../../../../styles/global.css';
import {
  AppointmentTypeSettings,
  ClientConfig as ClientConfigType,
  ClientFeatures as ClientFeatureType,
  FeatureValue,
  Stage,
} from '../../../../types';
import { CrmType } from '../../../../types/crmTypes';

export function ClientSettingsPage({ apiUrl }: { apiUrl: string }) {
  const { clientId } = useParams();
  return <ClientSettings apiUrl={apiUrl} key={clientId} />;
}

function ClientSettings({ apiUrl }: { apiUrl: string }) {
  const { clientId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [config, setConfig] = useState<ClientConfigType | undefined>();
  const [features, setFeatures] = useState<ClientFeatureType | undefined>();
  const [zillowCredentials, setZillowCredentials] = useState<
    CredentialConfig[]
  >([]);
  const [NumberOfEnabled, setNumberOfEnabled] = useState<number>(0);
  const [NumberOfAll, setNumberOfAll] = useState<number>(0);
  const [realtorCredentials, setRealtorCredentials] = useState<
    CredentialConfig[]
  >([]);
  const [stages, setStages] = useState<Stage[] | undefined>(undefined);
  const [appointmentTypes, setAppointmentTypes] = useState<
    AppointmentTypeSettings[] | undefined
  >(undefined);
  const [crmCredentials, setCrmCredentials] = useState<Record<string, string>>(
    {},
  );
  const user = useContext(UserContext);

  // Fetch CRM credentials for navigation and config
  useEffect(() => {
    const fetchCredentials = async () => {
      if (!user || !clientId) {
        return;
      }
      try {
        const response = await fetch(
          `${apiUrl}/api/settings/${clientId}/keys`,
          {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
            },
          },
        );
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch credentials: ${errorText}`);
        }
        const data = await response.json();
        setCrmCredentials(data);
      } catch (error) {
        // Optionally set error here
      }
    };
    void fetchCredentials();
  }, [apiUrl, clientId, user]);

  const fetchClientSettings = async () => {
    if (user == null) {
      return;
    }
    try {
      setLoading(true);
      setError(undefined);

      const response = await fetch(`${apiUrl}/api/settings/${clientId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Error fetching client settings: ${await response.text()}`,
        );
      }
      const results = await response.json();
      setConfig(results.config);
      setFeatures(results.features);
      setZillowCredentials(results.zillow_credentials);
      setRealtorCredentials(results.realtor_credentials);
      setStages(results.stages);
      setAppointmentTypes(results.appointment_types);
      setNumberOfEnabled(results.number_of_enabled);
      setNumberOfAll(results.number_of_all);
    } catch (error) {
      setError(`${error}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchClientSettings();
  }, [user?.accessToken]);

  const toggleFeature = async (feature: string) => {
    if (!user || !config) {
      return;
    }

    try {
      setError(undefined);
      const newValue =
        features?.[feature] === FeatureValue.ALL
          ? FeatureValue.DISABLED
          : FeatureValue.ALL;

      const response = await fetch(
        `${apiUrl}/api/settings/${config.id}/features/${feature}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
          body: JSON.stringify({ feature_value: newValue }),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to update feature: ${await response.text()}`);
      }

      setFeatures(prevFeatures => {
        if (prevFeatures === undefined) {
          return prevFeatures;
        }
        return { ...prevFeatures, [feature]: newValue };
      });
    } catch (error) {
      setError(`Error updating feature: ${error}`);
    }
  };

  const enableFeatures = async (featureNames: string[]) => {
    if (!user || !config) {
      return;
    }
    setError(undefined);

    try {
      for (const feature of featureNames) {
        const response = await fetch(
          `${apiUrl}/api/settings/${config.id}/features/${feature}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${user.accessToken}`,
            },
            body: JSON.stringify({ feature_value: FeatureValue.ALL }),
          },
        );

        if (!response.ok) {
          setError(`Failed to enable ${feature}: ${await response.text()}`);
          return;
        }

        setFeatures((prev: ClientFeatureType | undefined) =>
          prev ? { ...prev, [feature]: FeatureValue.ALL } : prev,
        );
      }
    } catch (error) {
      setError(`Error enabling sync flags: ${error}`);
    }
  };

  return (
    <>
      <UnauthenticatedTemplate>
        <SignInButton loginType="popup" />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AlertBanner error={error} setError={setError} />

        {config && (
          <ClientNavigation
            activeTab="settings"
            clientId={clientId}
            clientName={config.friendly_name}
            crmLink={config.referral_url}
            crmType={config.crm_type as CrmType | undefined}
            hasCrmCredentials={Object.values(crmCredentials).some(
              value => value !== 'No key',
            )}
            isSingleAgent={config.is_single_agent}
            numericClientId={config.client_id}
          />
        )}
        <Typography.Title
          level={4}
          style={{
            margin: '0 0 24px 0',
            fontSize: '22px',
            color: '#333',
          }}
        >
          Client Settings
        </Typography.Title>

        <Typography.Text
          style={{ display: 'block', margin: '-16px 0 24px 0' }}
          type="secondary"
        >
          Client ID: {config?.client_id}
        </Typography.Text>

        {loading && !config ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '40px 0',
            }}
          >
            <Spin size="large" />
          </div>
        ) : config && features && user ? (
          <>
            <Card
              bordered={false}
              style={{
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                marginBottom: '24px',
              }}
            >
              <ClientConfig
                accessToken={user.accessToken || ''}
                apiUrl={apiUrl}
                clientId={clientId || ''}
                config={config}
                crmCredentials={crmCredentials}
                features={features}
                NumberOfAll={NumberOfAll}
                numberOfEnabled={NumberOfEnabled}
                onConfigChange={fetchClientSettings}
                realtorCredentials={realtorCredentials}
                setError={setError}
                zillowCredentials={zillowCredentials}
              />
            </Card>

            <Divider style={{ margin: '32px 0' }} />

            <Card
              bordered={false}
              style={{
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                marginBottom: '24px',
              }}
              title="Client Features"
            >
              <ClientFeatures
                enableFeatures={enableFeatures}
                features={features}
                toggleFeature={(feature: string) => {
                  void toggleFeature(feature);
                }}
              />
            </Card>

            <Divider style={{ margin: '32px 0' }} />

            <Card
              bordered={false}
              style={{
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                marginBottom: '24px',
              }}
              title="Stages Configuration"
            >
              <StagesConfig
                accessToken={user.accessToken || ''}
                apiUrl={apiUrl}
                clientId={config.id}
                setError={setError}
                setStages={setStages}
                stages={stages || []}
              />
            </Card>

            <Divider style={{ margin: '32px 0' }} />

            <Card
              bordered={false}
              style={{
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                marginBottom: '24px',
              }}
              title="Appointments Configuration"
            >
              <AppointmentsConfig
                accessToken={user.accessToken || ''}
                apiUrl={apiUrl}
                appointments={appointmentTypes || []}
                clientId={config.id}
                setAppointments={setAppointmentTypes}
                setError={setError}
              />
            </Card>

            <Divider style={{ margin: '32px 0' }} />

            <Card
              bordered={false}
              style={{
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                marginBottom: '24px',
              }}
              title="Agent Management"
            >
              <AgentSection
                accessToken={user.accessToken || ''}
                apiUrl={apiUrl}
                clientConfig={config}
                clientConfigId={clientId || ''}
                clientFeatures={features}
                clientId={config.client_id}
                setError={setError}
              />
            </Card>

            <Divider style={{ margin: '32px 0' }} />

            <Card
              bordered={false}
              style={{
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                marginBottom: '24px',
              }}
              title="Branding"
            >
              <BrandingConfig
                accessToken={user.accessToken || ''}
                apiUrl={apiUrl}
                clientId={config.client_id}
                setError={setError}
              />
            </Card>
          </>
        ) : (
          <div
            className="error-message"
            style={{ padding: '20px', textAlign: 'center' }}
          >
            <Typography.Text type="danger">
              Error: Reach out to engineering or refresh the page
            </Typography.Text>
          </div>
        )}
      </AuthenticatedTemplate>
    </>
  );
}
